import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { FormSection } from '@/components/forms/Form';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect } from '@/components/forms/FormField';
import { FormCheckbox } from '@/components/forms/FormCheckbox';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import {
  useBanques,
  useCaisses,
  useComptesBancaires,
  useDeleteBanque,
  useDeleteCaisse,
  useDeleteCompte,
  useDevises,
  useUpsertBanque,
  useUpsertCaisse,
  useUpsertCompte,
  type UpsertBanqueInput,
  type UpsertCaisseInput,
  type UpsertCompteInput,
} from '@/hooks/queries/useBanques';
import { useRole } from '@/hooks/useRole';
import { formatDA } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type { BanqueRow, CaisseRow, CompteBancaireRow } from '@/lib/database.types';

type Tab = 'comptes' | 'caisses' | 'banques';

export function BanquesPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>('comptes');
  const { isAdmin } = useRole();
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState<string>('');

  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('parametres.banques.title')}
        subtitle={t('parametres.banques.subtitle')}
      />

      <div className="space-y-4 p-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-md border border-border bg-card p-0.5">
            {(['comptes', 'caisses', 'banques'] as Tab[]).map((k) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={cn(
                  'rounded-sm px-3 py-1.5 text-sm transition-colors',
                  tab === k
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t(`parametres.banques.tabs.${k}`)}
              </button>
            ))}
          </div>

          {(tab === 'comptes' || tab === 'caisses') && (
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
          )}
        </div>

        {tab === 'comptes' && <ComptesTab entrepriseId={entrepriseId} canEdit={isAdmin} />}
        {tab === 'caisses' && <CaissesTab entrepriseId={entrepriseId} canEdit={isAdmin} />}
        {tab === 'banques' && <BanquesTab canEdit={isAdmin} />}
      </div>
    </div>
  );
}

// ─── Comptes bancaires ────────────────────────────────────────────
function ComptesTab({
  entrepriseId,
  canEdit,
}: {
  entrepriseId: string;
  canEdit: boolean;
}): JSX.Element {
  const { data: comptes = [], isLoading } = useComptesBancaires(entrepriseId);
  const { data: banques = [] } = useBanques();
  const { data: devises = [] } = useDevises();
  const upsert = useUpsertCompte();
  const remove = useDeleteCompte();

  const [dialog, setDialog] = useState<{ open: boolean; editing: CompteBancaireRow | null }>({
    open: false,
    editing: null,
  });
  const [confirmDelete, setConfirmDelete] = useState<CompteBancaireRow | null>(null);

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="flex justify-end">
          <Button onClick={() => setDialog({ open: true, editing: null })}>
            <Plus className="h-4 w-4" />
            {t('parametres.banques.addCompte')}
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : comptes.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Aucun compte bancaire pour cette entreprise.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Banque</th>
                <th className="px-3 py-2 text-left">Intitulé</th>
                <th className="px-3 py-2 text-left">N° de compte</th>
                <th className="px-3 py-2 text-left">Devise</th>
                <th className="px-3 py-2 text-right">Solde initial</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {comptes.map((c) => {
                const banque = banques.find((b) => b.id === c.banque_id);
                return (
                  <tr key={c.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-3 py-2 text-xs">
                      {banque?.code ?? '?'} — {banque?.nom ?? ''}
                    </td>
                    <td className="px-3 py-2 font-medium">{c.intitule}</td>
                    <td className="px-3 py-2 font-mono text-xs">{c.numero_compte}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline">{c.devise_code}</Badge>
                    </td>
                    <td className="num px-3 py-2 text-right">{formatDA(c.solde_initial)}</td>
                    <td className="px-3 py-2">
                      {c.is_active ? (
                        <Badge variant="success">Actif</Badge>
                      ) : (
                        <Badge variant="outline">Inactif</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canEdit && (
                        <div className="inline-flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setDialog({ open: true, editing: c })}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={() => setConfirmDelete(c)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CompteFormDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog({ open: o, editing: dialog.editing })}
        editing={dialog.editing}
        entrepriseId={entrepriseId}
        banques={banques}
        devises={devises}
        onSubmit={async (v) => {
          await upsert.mutateAsync({
            id: dialog.editing?.id,
            entreprise_id: entrepriseId,
            ...v,
          } as UpsertCompteInput);
          toast.success(dialog.editing ? 'Compte mis à jour' : 'Compte créé');
          setDialog({ open: false, editing: null });
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer le compte « ${confirmDelete?.intitule} » ?`}
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await remove.mutateAsync(confirmDelete.id);
          toast.success('Compte supprimé');
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}

const compteSchema = z.object({
  banque_id: z.string().uuid(),
  intitule: z.string().min(2),
  numero_compte: z.string().min(2),
  rib: z.string().optional().nullable(),
  iban: z.string().optional().nullable(),
  devise_code: z.string().min(3),
  solde_initial: z.coerce.number(),
  is_active: z.boolean(),
});
type CompteForm = z.infer<typeof compteSchema>;

function CompteFormDialog({
  open,
  onOpenChange,
  editing,
  entrepriseId,
  banques,
  devises,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: CompteBancaireRow | null;
  entrepriseId: string;
  banques: BanqueRow[];
  devises: { code: string; libelle: string }[];
  onSubmit: (v: CompteForm) => Promise<void>;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CompteForm>({
    resolver: zodResolver(compteSchema),
    defaultValues: {
      banque_id: '',
      intitule: '',
      numero_compte: '',
      devise_code: 'DZD',
      solde_initial: 0,
      is_active: true,
    },
  });

  useEffect(() => {
    if (open) {
      reset(
        editing
          ? {
              banque_id: editing.banque_id,
              intitule: editing.intitule,
              numero_compte: editing.numero_compte,
              rib: editing.rib,
              iban: editing.iban,
              devise_code: editing.devise_code,
              solde_initial: editing.solde_initial,
              is_active: editing.is_active,
            }
          : {
              banque_id: banques[0]?.id ?? '',
              intitule: '',
              numero_compte: '',
              devise_code: 'DZD',
              solde_initial: 0,
              is_active: true,
            },
      );
    }
  }, [open, editing, reset, banques]);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={editing ? 'Modifier le compte' : t('parametres.banques.addCompte')}
      description="Entreprise courante : sélection en haut."
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(async (v) => {
        if (!entrepriseId) {
          toast.error('Choisissez une entreprise');
          return;
        }
        await onSubmit(v);
      })}
    >
      <FormSection>
        <FormSelect
          label="Banque"
          required
          value={watch('banque_id')}
          onChange={(v) => setValue('banque_id', v, { shouldValidate: true })}
          options={banques.map((b) => ({ value: b.id, label: `${b.code} — ${b.nom}` }))}
          error={errors.banque_id?.message}
        />
        <FormField
          label={t('parametres.banques.compte.intitule')}
          required
          {...register('intitule')}
          error={errors.intitule?.message}
        />
        <FormField
          label={t('parametres.banques.compte.numero')}
          required
          {...register('numero_compte')}
          error={errors.numero_compte?.message}
        />
        <FormField label={t('parametres.banques.compte.rib')} {...register('rib')} />
        <FormField label={t('parametres.banques.compte.iban')} {...register('iban')} />
        <FormSelect
          label={t('parametres.banques.compte.devise')}
          required
          value={watch('devise_code')}
          onChange={(v) => setValue('devise_code', v, { shouldValidate: true })}
          options={devises.map((d) => ({ value: d.code, label: `${d.code} — ${d.libelle}` }))}
        />
        <FormField
          type="number"
          step="0.01"
          label={t('parametres.banques.compte.soldeInitial')}
          required
          {...register('solde_initial')}
        />
        <FormCheckbox label="Compte actif" {...register('is_active')} />
      </FormSection>
    </FormDialog>
  );
}

// ─── Caisses ──────────────────────────────────────────────────────
function CaissesTab({
  entrepriseId,
  canEdit,
}: {
  entrepriseId: string;
  canEdit: boolean;
}): JSX.Element {
  const { data: caisses = [], isLoading } = useCaisses(entrepriseId);
  const { data: devises = [] } = useDevises();
  const upsert = useUpsertCaisse();
  const remove = useDeleteCaisse();

  const [dialog, setDialog] = useState<{ open: boolean; editing: CaisseRow | null }>({
    open: false,
    editing: null,
  });
  const [confirmDelete, setConfirmDelete] = useState<CaisseRow | null>(null);

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="flex justify-end">
          <Button onClick={() => setDialog({ open: true, editing: null })}>
            <Plus className="h-4 w-4" /> {t('parametres.banques.addCaisse')}
          </Button>
        </div>
      )}
      {isLoading ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : caisses.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Aucune caisse.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {caisses.map((c) => (
            <article key={c.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h4 className="font-semibold">{c.nom}</h4>
                  {c.site && <p className="text-xs text-muted-foreground">{c.site}</p>}
                </div>
                <Badge variant="outline">{c.devise_code}</Badge>
              </div>
              <div className="mt-2">
                <div className="text-[10px] uppercase text-muted-foreground">Solde initial</div>
                <div className="num text-base font-medium">{formatDA(c.solde_initial)}</div>
              </div>
              {canEdit && (
                <div className="mt-3 flex justify-end gap-1 border-t border-border pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDialog({ open: true, editing: c })}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive"
                    onClick={() => setConfirmDelete(c)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
      <CaisseFormDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog({ open: o, editing: dialog.editing })}
        editing={dialog.editing}
        entrepriseId={entrepriseId}
        devises={devises}
        onSubmit={async (v) => {
          await upsert.mutateAsync({
            id: dialog.editing?.id,
            entreprise_id: entrepriseId,
            ...v,
          } as UpsertCaisseInput);
          toast.success(dialog.editing ? 'Caisse mise à jour' : 'Caisse créée');
          setDialog({ open: false, editing: null });
        }}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer la caisse « ${confirmDelete?.nom} » ?`}
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await remove.mutateAsync(confirmDelete.id);
          toast.success('Caisse supprimée');
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}

const caisseSchema = z.object({
  nom: z.string().min(2),
  site: z.string().optional().nullable(),
  devise_code: z.string().min(3),
  solde_initial: z.coerce.number(),
  is_active: z.boolean(),
});
type CaisseForm = z.infer<typeof caisseSchema>;

function CaisseFormDialog({
  open,
  onOpenChange,
  editing,
  entrepriseId,
  devises,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: CaisseRow | null;
  entrepriseId: string;
  devises: { code: string; libelle: string }[];
  onSubmit: (v: CaisseForm) => Promise<void>;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CaisseForm>({
    resolver: zodResolver(caisseSchema),
    defaultValues: { nom: '', devise_code: 'DZD', solde_initial: 0, is_active: true },
  });

  useEffect(() => {
    if (open) {
      reset(
        editing
          ? {
              nom: editing.nom,
              site: editing.site,
              devise_code: editing.devise_code,
              solde_initial: editing.solde_initial,
              is_active: editing.is_active,
            }
          : { nom: '', devise_code: 'DZD', solde_initial: 0, is_active: true },
      );
    }
  }, [open, editing, reset]);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={editing ? 'Modifier la caisse' : t('parametres.banques.addCaisse')}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(async (v) => {
        if (!entrepriseId) {
          toast.error('Choisissez une entreprise');
          return;
        }
        await onSubmit(v);
      })}
    >
      <FormSection>
        <FormField
          label="Nom de la caisse"
          required
          {...register('nom')}
          error={errors.nom?.message}
        />
        <FormField label="Site / agence" {...register('site')} />
        <FormSelect
          label="Devise"
          required
          value={watch('devise_code')}
          onChange={(v) => setValue('devise_code', v, { shouldValidate: true })}
          options={devises.map((d) => ({ value: d.code, label: `${d.code} — ${d.libelle}` }))}
        />
        <FormField
          type="number"
          step="0.01"
          label="Solde initial"
          required
          {...register('solde_initial')}
        />
        <FormCheckbox label="Caisse active" {...register('is_active')} fullWidth />
      </FormSection>
    </FormDialog>
  );
}

// ─── Référentiel banques ──────────────────────────────────────────
function BanquesTab({ canEdit }: { canEdit: boolean }): JSX.Element {
  const { data: banques = [], isLoading } = useBanques();
  const upsert = useUpsertBanque();
  const remove = useDeleteBanque();
  const [dialog, setDialog] = useState<{ open: boolean; editing: BanqueRow | null }>({
    open: false,
    editing: null,
  });
  const [confirmDelete, setConfirmDelete] = useState<BanqueRow | null>(null);

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="flex justify-end">
          <Button onClick={() => setDialog({ open: true, editing: null })}>
            <Plus className="h-4 w-4" /> {t('parametres.banques.addBanque')}
          </Button>
        </div>
      )}
      {isLoading ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Nom</th>
                <th className="px-3 py-2 text-left">Pays</th>
                <th className="px-3 py-2 text-left">SWIFT</th>
                <th className="px-3 py-2 text-left">Statut</th>
                <th className="px-3 py-2 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {banques.map((b) => (
                <tr key={b.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                  <td className="px-3 py-2 font-mono text-xs">{b.code}</td>
                  <td className="px-3 py-2 font-medium">
                    {b.nom}
                    {b.is_system && (
                      <Badge
                        variant="outline"
                        className="ml-2 inline-flex items-center gap-1 text-[10px]"
                      >
                        <Lock className="h-2.5 w-2.5" /> {t('common.system')}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{b.pays}</td>
                  <td className="px-3 py-2 font-mono text-xs">{b.swift ?? '—'}</td>
                  <td className="px-3 py-2">
                    {b.is_active ? (
                      <Badge variant="success">Actif</Badge>
                    ) : (
                      <Badge variant="outline">Inactif</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && !b.is_system && (
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setDialog({ open: true, editing: b })}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive"
                          onClick={() => setConfirmDelete(b)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BanqueFormDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog({ open: o, editing: dialog.editing })}
        editing={dialog.editing}
        onSubmit={async (v) => {
          await upsert.mutateAsync({ id: dialog.editing?.id, ...v } as UpsertBanqueInput);
          toast.success(dialog.editing ? 'Banque mise à jour' : 'Banque ajoutée');
          setDialog({ open: false, editing: null });
        }}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer la banque « ${confirmDelete?.nom} » ?`}
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await remove.mutateAsync(confirmDelete.id);
          toast.success('Banque supprimée');
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}

const banqueSchema = z.object({
  code: z.string().min(2).max(20),
  nom: z.string().min(2),
  pays: z.string().min(2),
  swift: z.string().optional().nullable(),
  is_active: z.boolean(),
});
type BanqueForm = z.infer<typeof banqueSchema>;

function BanqueFormDialog({
  open,
  onOpenChange,
  editing,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: BanqueRow | null;
  onSubmit: (v: BanqueForm) => Promise<void>;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<BanqueForm>({
    resolver: zodResolver(banqueSchema),
    defaultValues: { code: '', nom: '', pays: 'Algérie', is_active: true },
  });

  useEffect(() => {
    if (open) {
      reset(
        editing
          ? {
              code: editing.code,
              nom: editing.nom,
              pays: editing.pays,
              swift: editing.swift,
              is_active: editing.is_active,
            }
          : { code: '', nom: '', pays: 'Algérie', is_active: true },
      );
    }
  }, [open, editing, reset]);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={editing ? 'Modifier la banque' : t('parametres.banques.addBanque')}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(async (v) => {
        await onSubmit(v);
      })}
    >
      <FormSection>
        <FormField
          label="Code"
          required
          {...register('code')}
          hint="ex. WISE, BNP, SG…"
          error={errors.code?.message}
        />
        <FormField label="Nom" required {...register('nom')} error={errors.nom?.message} />
        <FormField label="Pays" required {...register('pays')} />
        <FormField label="SWIFT / BIC" {...register('swift')} />
        <FormCheckbox label="Banque active" {...register('is_active')} fullWidth />
      </FormSection>
    </FormDialog>
  );
}
